import ahoy from "ahoy.js";
import { StorefrontProductPageProps } from "@/types";
import { Link, usePage } from "@inertiajs/react";
import { ReactNode, useLayoutEffect } from "react";
import MetaTags from "@/components/application/meta-tags";
import ImageWithFallback from "@/components/application/image-with-fallback";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { storefront_store_root_path } from "@/lib/routes";
import StorefrontLayout from "@/components/application/storefront-layout";
import useTranslations from "@/hooks/use-translations";
import { getProductState, getProductTypeLabel } from "@/lib/utils";

function ProductPage() {
  const { current_store, product } =
    usePage<StorefrontProductPageProps>().props;
  const { t } = useTranslations("storefront.products.show");

  useLayoutEffect(() => {
    ahoy.track("Viewed store", {
      store_id: current_store.id,
      product_id: product.id,
    });
  }, [current_store.id]);

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href={storefront_store_root_path()}>{t("home")}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {getProductTypeLabel(product.product_type, t)}
            </BreadcrumbPage>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{product.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="px-4 overflow-auto w-full max-w-5xl mx-auto">
        <div className=" grid grid-cols-2 gap-8">
          <ImageWithFallback
            srcset={[product.custom_image_url, product.image_url]}
            alt={product.name}
            className="relative w-full rounded-sm m-auto object-contain aspect-square"
          />
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <Badge variant="outline">{product.system_code}</Badge>{" "}
              <Badge variant="outline">{getProductState(product, t)}</Badge>
            </div>

            <h2 className="text-3xl font-semibold">{product.name}</h2>
            <span className="text-2xl">
              {new Intl.NumberFormat(navigator.language, {
                style: "currency",
                currency: current_store.currency.toUpperCase() || "USD",
              }).format(product.price_cents / 100)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

ProductPage.layout = (page: ReactNode) => <StorefrontLayout children={page} />;

export default ProductPage;
